// FOR REVIEW - review this whole file and all it's endpoints
// src/store.js
import { createSlice, createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { axiosInstance } from "services/axios-supabase";
import _ from "lodash";
import moment from "moment";
import { supabase } from "services/supabase";

/** RFC3339 with local offset (e.g. 2025-04-01T00:00:00.000-04:00). Same instants as toISOString(); reads like local wall time + offset. */
const toRFC3339WithOffset = (m) =>
  m.format("YYYY-MM-DD[T]HH:mm:ss.SSS") + m.format("Z");

const getDefaultParams = () => {
  const weekStart = moment().startOf("week");
  return {
    view: "week",
    effectiveDate: weekStart.format("YYYY/MM/DD"),
    client: null,
    q: "",
  };
};

const loadQueryParams = () => {
  try {
    const stored = localStorage.getItem("queryParams");
    if (!stored) {
      return getDefaultParams(); // Week view, current date
    }

    const parsed = JSON.parse(stored);
    const now = Date.now();
    const EIGHT_HOURS = 8 * 60 * 60 * 1000;

    // Handle old format (no savedAt) - treat as expired
    if (!parsed.savedAt) {
      // Old format: params stored directly as object
      // Treat as expired and return defaults
      return getDefaultParams();
    }

    // New format: { params: {...}, savedAt: timestamp }
    const { params, savedAt } = parsed;

    // Check if saved params are less than 8 hours old
    if (savedAt && now - savedAt < EIGHT_HOURS) {
      return params; // Use stored params
    }

    // Expired - return defaults
    return getDefaultParams();
  } catch (error) {
    console.error("Error loading query params:", error);
    return getDefaultParams();
  }
};

/** Highest event_count first (matches API); stable tie-break on connection + calendar id. */
const sortLinkedCalendarsByEventCountDesc = (linked) => {
  if (!Array.isArray(linked) || linked.length === 0) return linked;
  return [...linked].sort((a, b) => {
    const da = Number(a.event_count ?? 0);
    const db = Number(b.event_count ?? 0);
    if (db !== da) return db - da;
    const ca = `${a.connection_id ?? ""}\0${a.calendar_id ?? ""}`;
    const cb = `${b.connection_id ?? ""}\0${b.calendar_id ?? ""}`;
    return ca.localeCompare(cb);
  });
};

// ------------------------------------------------------------------------
// Initial state
const initialState = {
  report: null,
  events: [],
  totals: [],
  options: {},
  hasToken: false,
  loading: true,
  error: null,
  /** Debug-only `/events` filters (not persisted with queryParams). */
  debugEventsQuery: {
    connectionId: "",
    calendarIds: "",
    linkedCalendarIds: "",
  },
  /** Last /events `linked_calendars` (debug: copy UUIDs into linked_calendar_ids). */
  debugLastLinkedCalendars: null,
  params: loadQueryParams(),
  eventsLoaded: false,
  reportLoaded: false,
  sortOrder: "asc",
  bulkResults: {},
  calendarConnection: false,
  utilization: null,
  invoiceTimeline: null,
  invoiceTimelineLoading: false,
  invoiceTimelineError: null,
  createEventLoading: false,
  createEventError: null,
};

// ------------------------------------------------------------------------

export const selectEventsLoaded = (state) => state.calendarReducer.eventsLoaded;

export const checkForCalendarConnection = createAsyncThunk(
  "data/checkForCalendarConnection",
  async (userId, { getState, rejectWithValue }) => {
    try {
      if (!userId) {
        throw new Error("User not authenticated");
      }

      const { data, error } = await supabase
        .from("calendars")
        .select("*")
        .eq("uid", userId)
        .limit(1)
        .maybeSingle();

      if (error && error.code !== "PGRST116") {
        // console.log("checkForCalendarConnection: Error", error);
        // PGRST116 is "no rows found", which is fine for our check
        throw error;
      }

      const response = { data };
      // Check if there's at least one calendar connection record
      if (Array.isArray(response.data) && response.data.length === 0) {
        return false;
      }
      return response.data;
    } catch (error) {
      // console.log("checkForCalendarConnection: Error", error);
      return rejectWithValue(error.message);
    }
  },
);

export const fetchEvents = createAsyncThunk(
  "data/fetchEvents",
  async (_, { getState, rejectWithValue }) => {
    try {
      let queryParams = getState().calendarReducer.params;

      // Default view when missing; persisted localStorage can include stale `from`/`to` (e.g. …Z)
      // from older builds or the API echo — those must not survive when `switch` hits `default`.
      const params = { view: "week", ...queryParams };
      params.view = params.view || "week";
      if (params.view !== "custom") {
        delete params.from;
        delete params.to;
      }

      switch (params.view) {
        case "day":
          if (params.effectiveDate) {
            const options = params.effectiveDate.split("/");
            params.from = toRFC3339WithOffset(
              moment()
                .year(options[0])
                .month(options[1] - 1)
                .date(options[2])
                .startOf("day"),
            );
            params.to = toRFC3339WithOffset(
              moment()
                .year(options[0])
                .month(options[1] - 1)
                .date(options[2])
                .endOf("day"),
            );
          } else {
            params.from = toRFC3339WithOffset(moment().startOf("day"));
            params.to = toRFC3339WithOffset(moment().endOf("day"));
          }
          break;
        case "week":
          if (params.effectiveDate) {
            const options = params.effectiveDate.split("/");
            const weekStart = moment()
              .year(options[0])
              .month(options[1] - 1)
              .date(options[2])
              .startOf("week");
            params.from = toRFC3339WithOffset(weekStart);
            // End of week should be 6 days after start (Sunday), set to end of Sunday in UTC
            // Convert weekStart to UTC first, then add 6 days and set to end of day
            const weekStartUTC = moment.utc(weekStart);
            const weekEnd = weekStartUTC.clone().add(6, "days").endOf("day");
            params.to = toRFC3339WithOffset(weekEnd);
          } else {
            const weekStart = moment().startOf("week");
            params.from = toRFC3339WithOffset(weekStart);
            // End of week should be 6 days after start (Sunday), set to end of Sunday in UTC
            // Convert weekStart to UTC first, then add 6 days and set to end of day
            const weekStartUTC = moment.utc(weekStart);
            const weekEnd = weekStartUTC.clone().add(6, "days").endOf("day");
            params.to = toRFC3339WithOffset(weekEnd);
          }
          break;
        case "month":
          if (params.effectiveDate) {
            const options = params.effectiveDate.split("/");
            const y = Number.parseInt(options[0], 10);
            const monthIndex = Number.parseInt(options[1], 10) - 1;
            // User calendar month in *local* time (e.g. 4/1 00:00–4/30 23:59:59.999) as RFC3339 + offset.
            const monthStart = moment([y, monthIndex, 1]).startOf("month");
            const monthEnd = moment([y, monthIndex, 1]).endOf("month");
            params.from = toRFC3339WithOffset(monthStart);
            params.to = toRFC3339WithOffset(monthEnd);
          } else {
            const monthStart = moment().startOf("month");
            const monthEnd = moment().endOf("month");
            params.from = toRFC3339WithOffset(monthStart);
            params.to = toRFC3339WithOffset(monthEnd);
          }
          break;
        case "year":
          if (params.effectiveDate) {
            const options = params.effectiveDate.split("/");
            params.from = toRFC3339WithOffset(
              moment().year(options[0]).startOf("year"),
            );
            params.to = toRFC3339WithOffset(
              moment().year(options[0]).endOf("year"),
            );
          } else {
            const n = moment();
            params.from = toRFC3339WithOffset(n.clone().startOf("year"));
            params.to = toRFC3339WithOffset(n.clone().endOf("year"));
          }
          break;
        case "custom":
          params.from = toRFC3339WithOffset(
            moment(params.from).startOf("day"),
          );
          params.to = toRFC3339WithOffset(moment(params.to).endOf("day"));
          break;
        default: {
          // Unknown view: same range as week so /events always gets offset strings from this thunk.
          const weekStart = params.effectiveDate
            ? moment(params.effectiveDate, "YYYY/MM/DD").startOf("week")
            : moment().startOf("week");
          params.from = toRFC3339WithOffset(weekStart);
          const weekStartUTC = moment.utc(weekStart);
          const weekEnd = weekStartUTC.clone().add(6, "days").endOf("day");
          params.to = toRFC3339WithOffset(weekEnd);
          break;
        }
      }

      const dbg = getState().calendarReducer.debugEventsQuery || {};
      const conn = String(dbg.connectionId || "").trim();
      if (conn) params.connection_id = conn;
      const calIds = String(dbg.calendarIds || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (calIds.length) params.calendar_ids = calIds;
      const linkedIds = String(dbg.linkedCalendarIds || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (linkedIds.length) params.linked_calendar_ids = linkedIds;

      const response = await axiosInstance.get("/events", {
        params,
      });

      return response.data;
    } catch (error) {
      console.log("Error in fetchEvents", error.message);
      return rejectWithValue(error.message);
    }
  },
);

export const updateEvent = createAsyncThunk(
  "data/updateEvent",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/events/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteEvent = createAsyncThunk(
  "data/deleteEvent",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/events/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const createEvent = createAsyncThunk(
  "data/createEvent",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/events", payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// ------------------------------------------------------------------------

export const fetchReport = createAsyncThunk(
  "data/fetchReport",
  async (params, { getState, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/events/reports", { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// ------------------------------------------------------------------------

export const bulkUpdate = createAsyncThunk(
  "data/bulkUpdateSocket",
  async (options, { getState, dispatch, rejectWithValue }) => {
    try {
      // Set up WebSocket connection
      const url = new URL(import.meta.env.VITE_API_URL);
      const protocol = url.protocol === "https:" ? "wss:" : "ws:";
      const socket = new WebSocket(`${protocol}//${url.host}/ws`);

      let clientId;

      // Add event listener to receive messages
      socket.onmessage = (event) => {
        const message = JSON.parse(event.data);
        if (message.type && message.type === "eventUpdate") {
          console.log("Received eventUpdate:", typeof message, message);
          // Dispatch the updateEventDirectly action with the message data
          dispatch(
            updateEventDirectly({ id: message.data.id, data: message.data }),
          );
        }
      };

      // Create a promise that resolves when the WebSocket is open
      const clientIdPromise = new Promise((resolve) => {
        socket.onopen = () => {
          console.log("WebSocket connection established");
          clientId = generateClientId();
          socket.send(JSON.stringify({ clientId }));
          resolve(clientId);
        };
      });

      // Wait for the clientId to be ready
      clientId = await clientIdPromise;

      // Include clientId in the POST request headers
      const response = await axiosInstance.post("/events/bulk", options, {
        headers: {
          "Client-Id": clientId, // Include the clientId in the headers
        },
      });

      console.log("Data from REST API:", response.data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// Function to generate a clientId (example implementation)
const generateClientId = () => {
  return "client-" + Math.random().toString(36).substr(2, 9); // Example clientId generation
};

// ------------------------------------------------------------------------

export const exportCalendarData = createAsyncThunk(
  "data/exportCalendarData",
  async ({ connection_id, calendar_id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/events/dump", {
        connection_id: connection_id,
        calendar_id: calendar_id,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Fetch invoice timeline
export const fetchInvoiceTimeline = createAsyncThunk(
  "data/fetchInvoiceTimeline",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/invoices/timeline");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to load invoice timeline",
      );
    }
  },
);

// ------------------------------------------------------------------------
// Slices

export const CalendarSlice = createSlice({
  name: "data",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setQueryParam: (state, action) => {
      state.params[action.payload.key] = action.payload.value;

      // Save with timestamp
      const storageData = {
        params: state.params,
        savedAt: Date.now(),
      };
      localStorage.setItem("queryParams", JSON.stringify(storageData));
    },
    resetQueryParams: (state) => {
      return {};
    },
    resetEventsLoaded: (state) => {
      state.eventsLoaded = false; // Add a reducer to reset the loaded flag if needed
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload; // Update the sortOrder based on the action payload
    },
    clearCreateEventError: (state) => {
      state.createEventError = null;
    },
    setDebugEventsQuery: (state, action) => {
      state.debugEventsQuery = {
        ...state.debugEventsQuery,
        ...action.payload,
      };
    },
    clearDebugEventsQuery: (state) => {
      state.debugEventsQuery = {
        connectionId: "",
        calendarIds: "",
        linkedCalendarIds: "",
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // ----------------------------------------------------------------
      // Reports
      .addCase(fetchReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchReport.fulfilled, (state, action) => {
        state.loading = false;
        state.report = action.payload;
        state.reportLoaded = true;
      })
      .addCase(fetchReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    // ----------------------------------------------------------------

    builder.addCase(fetchEvents.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    builder.addCase(fetchEvents.fulfilled, (state, action) => {
      state.hasToken = action.payload.hasToken;
      state.events = action.payload.events;
      state.totals = action.payload.totals;
      state.options = action.payload.options;
      state.utilization = action.payload.utilization || null;
      {
        const raw = action.payload.linked_calendars ?? null;
        state.debugLastLinkedCalendars =
          raw == null ? null : sortLinkedCalendarsByEventCountDesc(raw);
      }
      state.loading = false;
      state.eventsLoaded = true;
    });

    builder.addCase(fetchEvents.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
      // Set eventsLoaded to true even on error, so dashboard doesn't stay in loading state
      state.eventsLoaded = true;
    });

    // ----------------------------------------------------------------
    // Invoice Timeline
    builder.addCase(fetchInvoiceTimeline.pending, (state) => {
      state.invoiceTimelineLoading = true;
      state.invoiceTimelineError = null;
    });
    builder.addCase(fetchInvoiceTimeline.fulfilled, (state, action) => {
      state.invoiceTimelineLoading = false;
      state.invoiceTimeline = action.payload;
      state.invoiceTimelineError = null;
    });
    builder.addCase(fetchInvoiceTimeline.rejected, (state, action) => {
      state.invoiceTimelineLoading = false;
      state.invoiceTimelineError = action.payload;
    });

    // ----------------------------------------------------------------
    builder.addCase(updateEvent.fulfilled, (state, action) => {
      const index = state.events.findIndex(
        (event) => event.id === action.payload.id,
      );
      if (index !== -1) {
        state.events[index] = Object.assign(
          {},
          state.events[index],
          action.payload,
        );
      }
      state.loading = false;
    });
    builder.addCase(deleteEvent.fulfilled, (state, action) => {
      const index = state.events.findIndex(
        (event) => event.id === action.payload.id,
      );
      if (index !== -1) {
        state.events[index] = Object.assign(
          {},
          state.events[index],
          action.payload,
        );
      }
      state.loading = false;
    });

    builder.addCase(createEvent.pending, (state) => {
      state.createEventLoading = true;
      state.createEventError = null;
    });
    builder.addCase(createEvent.fulfilled, (state, action) => {
      const event = action.payload?.event ?? action.payload;
      if (event && !state.events.find((e) => e.id === event.id)) {
        state.events.push(event);
      }
      state.createEventLoading = false;
      state.createEventError = null;
    });
    builder.addCase(createEvent.rejected, (state, action) => {
      state.createEventLoading = false;
      state.createEventError = action.payload;
    });

    // ---------------------------------------------------------------
    builder.addCase(bulkUpdate.pending, (state) => {
      state.bulkResults = {};
    });

    builder.addCase(bulkUpdate.fulfilled, (state, action) => {
      state.bulkResults = action.payload.data;
    });

    builder.addCase(bulkUpdate.rejected, (state, action) => {
      state.error = action.payload;
    });
    // ---------------------------------------------------------------
    builder.addCase(exportCalendarData.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(exportCalendarData.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(exportCalendarData.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    // ---------------------------------------------------------------
    builder.addCase(updateEventDirectly, (state, action) => {
      const index = state.events.findIndex(
        (event) => event.id === action.payload.id,
      );
      if (index !== -1) {
        state.events = [
          ...state.events.slice(0, index),
          { ...state.events[index], ...action.payload.data },
          ...state.events.slice(index + 1),
        ];
      }
    });
    builder.addCase(checkForCalendarConnection.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(checkForCalendarConnection.fulfilled, (state, action) => {
      // Convert to boolean: true if calendar data exists, false otherwise
      state.calendarConnection = !!action.payload;
    });
    builder.addCase(checkForCalendarConnection.rejected, (state, action) => {
      state.calendarConnection = false;
    });
  },
});

// Export actions and reducers
export const {
  setLoading,
  setError,
  setQueryParam,
  setSortOrder,
  setCalendarConnection,
  clearCreateEventError,
  setDebugEventsQuery,
  clearDebugEventsQuery,
} = CalendarSlice.actions;

// ------------------------------------------------------------------------
// Configure store

export default CalendarSlice.reducer;

// Add the updateEventDirectly action
export const updateEventDirectly = createAction("data/updateEventDirectly");
